import React from "react";
import { terms } from "../constants";
import { listingViews as lv } from "../constants/listingViews";
import {
    dayFirstDateFormat,
    formatPercentage,
    formatUnits,
    quarterNumYearFormat,
    shortMonthAndShortYearFormat
} from "../../agent-production/helpers/dataFormatters";
import { getISOWeek } from "date-fns";
import CustomHeaderKeyInfo from "../../common/components/table/agGrid/CustomHeaderKeyInfo";
import { marketDynamicsColumns as mdCols } from '../constants/marketDynamicsColumns';
import { periodKeys } from "../constants";
import { getTrendAnalysis } from "./trendAnalysis";
import { useMarketDynamicsStore } from "../../store/marketDynamics/store";
import { views } from "../../agent-production/constants/savedAgents";
import { agentProductionTerms, marketDynamicsTerms as mdTerms, marketDynamicsTimePeriods as mdtp,
    searchTerms } from "../../constants";
import { agentsColumns, tableViewNames } from "../../agent-production/constants/agentProductionConstants";
import { chartLegends as legend, labels, listingIds, listingOptions, overviewPeriods, tabNames,
    optionLabels
} from "../constants/listingViews/overview";
import { listingStatuses } from "../constants/listingStatuses";
import { supplyAmpDemandNumUnits } from "../constants/listingViews/supplyAmpDemandNumUnits";
import { getPercentChange } from "../../common/helpers/utilities";

export const hasAllZeroValues = (data, keysToDisregard) => {
    return data?.every(obj => {
        for (let key in obj) {
            if (!keysToDisregard.includes(key)) {
                if (typeof obj[key] === 'number' && !isNaN(obj[key]) && obj[key] !== 0) {
                    return false;
                }
            }
        }
        return true;
    });
};

export const getConfigData = (listing, config) => {
    let listingConfig, groupConfig;
    config?.find((item) => {
        groupConfig = item;
        listingConfig = item?.options.find((option) => option.value === listing);
        return listingConfig;
    });
    return { listingConfig, groupConfig };
};

const calculatePercentChange = (start, end) => {
    if (start === 0) return 0;
    return Math.abs(((end - start) / start)) * 100;
};

export const getTransformData = (searchResults) => {
    if (!searchResults) return;
    const getTransformedData = (prev) => {
        const { forSaleUnits, forSaleLastDayUnits } = prev;
        return {
            ...prev,
            forSaleLastDayChangeVolume: forSaleUnits - forSaleLastDayUnits,
            forSaleLastDayPercentage: calculatePercentChange(forSaleUnits, forSaleLastDayUnits)
        }
    };
    if(!searchResults.length) return getTransformedData(searchResults);
    return searchResults?.map(searchResult => {
        return getTransformedData(searchResult);
    });
};

export const getTransformKeyFields = (options, listingViewId) => {
    if (listingViewId !== lv.cdomBreakout.value) return options;
    const checkedWithRadio = options?.find(obj => obj?.checked && obj?.isRadio);
    if (!checkedWithRadio) return [];

    const revisedOptions = [];

    options.forEach(obj => {
        if (obj?.checked && !obj?.isRadio) {
            revisedOptions.push({
                ...obj,
                keyField: checkedWithRadio.keyField + obj.keyFieldComplement,
                keyInfoLabel: `${checkedWithRadio.label} ${obj.label} ${terms.cdom}`
            });
        } else {
            revisedOptions.push({ ...obj });
        }
    });

    return revisedOptions;
};

export const getChartTooltipContent = (data, series, dataPointIndex, barColors, config) => {
    const checkedStatusOptions = useMarketDynamicsStore.getState().checkedStatusOptions;
    const tooltipHeadersByPeriod = {
        [terms.weekOf]: `Week ${getISOWeek(new Date(data[dataPointIndex].timePeriodStart))} <br />
            ${dayFirstDateFormat(data[dataPointIndex].timePeriodStart)} - ${dayFirstDateFormat(data[dataPointIndex].timePeriodEnd)}`,
        [terms.month]: shortMonthAndShortYearFormat(data[dataPointIndex].timePeriodStart),
        [terms.quarter]: quarterNumYearFormat(data[dataPointIndex].timePeriodStart)
    };

    const tooltipHeader = tooltipHeadersByPeriod[config.groupConfig.columnSettings.label];

    const popOverDataFormatter = config?.listingConfig?.chart?.popOverDataFormatter;
    if (!popOverDataFormatter) return `<div />`;

    const tooltipBody = checkedStatusOptions.map((option, index) =>
        `<span class='bullet' style='background-color: ${barColors[index]}'}></span>
                ${option?.label}: ${popOverDataFormatter(series[index][dataPointIndex])} <br />`
    );

    return `<div class='arrow_box volume-chart-tooltip'>
                <div class='header'>
                    ${tooltipHeader}
                </div>
                <div class='body'>
                <div class='items'>
                    ${tooltipBody.join('')}
                </div>
                </div>
            </div>`;
};

export const getHeaderAndTooltip = (baseTooltip, ...params) => {
    if (typeof baseTooltip?.tip !== "function") {
        const headerDetails = {
            ...baseTooltip,
            tip: baseTooltip?.tip
        };
        return { titleWithTooltip: <CustomHeaderKeyInfo isStatus={baseTooltip.label === mdCols.status.label} headerDetails={headerDetails} /> };
    }

    const tooltip = baseTooltip.tip(...params);

    const headerDetails = {
        ...baseTooltip,
        tip: tooltip
    };

    return { titleWithTooltip: <CustomHeaderKeyInfo isStatus={baseTooltip.label === mdCols.status.label} headerDetails={headerDetails} /> };
};

const getPercentageChange = (changeVolume, beginValue) => {
    if (changeVolume.data === 0) return '0';
    if (!beginValue) return '';
    return changeVolume.data / beginValue;
};

const getAvg = (data, keyField) => {
    const dataForAvg = data?.map((x) => x[keyField]).filter((x) => x !== undefined);
    return dataForAvg?.reduce((acc, curr) => acc + curr, 0) / dataForAvg?.length;
};

const getChange = (beginValue, endValue, formatter) => {
    if (!endValue || !beginValue) return '';
    return {
        formatter,
        data: endValue - beginValue
    };
};

export const getCleanedValue = (value) => {
    if (typeof value === 'number') return value;
    return value && parseFloat(value.replace(/[$,%]/g, ''));
}; 

export const getQuarterlyOrMonthlyData = ({ selectedPeriodComparison, beginPeriodData, endPeriodData, searchResults,
    featureToggles, id, statusLabel, keyField, formatter, changeFormatter, isOverview
}) => {

    if (selectedPeriodComparison === periodKeys.wholeTimePeriod) {
        if (beginPeriodData && endPeriodData) {
            const filteredSearchResults = searchResults?.filter(searchResult => new Date(searchResult.timePeriodStart)
                >= new Date(beginPeriodData?.timePeriodStart)
                && new Date(searchResult.timePeriodStart) <= new Date(endPeriodData?.timePeriodStart));
            return getTrendAnalysis({ filteredSearchResults, id, statusLabel, keyField, featureToggles, formatter });
        }
    }
    const endValue = isOverview ? endPeriodData?.[keyField] : String(formatter(endPeriodData?.[keyField]) ?? 0);
    const beginValue = isOverview ? beginPeriodData?.[keyField] : String(formatter(beginPeriodData?.[keyField]) ?? 0);
    const changeValue = getChange(getCleanedValue(beginValue), getCleanedValue(endValue), changeFormatter);
    const changeValuePercentage = getPercentageChange(changeValue, getCleanedValue(beginValue));
    return {
        id,
        status: statusLabel,
        beginPeriodValue: beginValue,
        endPeriodValue: endValue,
        changeValue,
        changeValuePercentage
    };
};

export const getWeeklyData = (formatter, previousWeeksData, lastWeeksData, keyField, changeFormatter, id, label) => {
    if (!previousWeeksData?.length || !lastWeeksData?.length) {
        return { id, status: label };
    }

    const previousWeeksAvg = formatter(getAvg(previousWeeksData, keyField));
    const lastWeeksAvg = formatter(getAvg(lastWeeksData, keyField));
    const changeAvg = getChange(getCleanedValue(previousWeeksAvg), getCleanedValue(lastWeeksAvg), changeFormatter);
    const changeAvgPercentage = getPercentageChange(changeAvg, getCleanedValue(previousWeeksAvg));
    return {
        id,
        status: label,
        previousWeeksAvg,
        lastWeeksAvg,
        changeAvg,
        changeAvgPercentage
    };
};

export const getBreakdownPinnedRowColumns = (columns) => {
    return columns.map(col => {
        if (col.col.id === mdCols.timePeriodStart.id) return mdCols.officeName.id;
        return col.col?.keyField || col.col.id;
    });
};

const viewsToReplaceAgentName = [
    views.overview,
    views.listingProficiency
];

const viewsToReplaceOfficeName = [
    lv.medianPrice.value,
    lv.soldAvgVsMedianPrice.value,
    lv.avg$Sqft.value,
    lv.avgSpOpComparison.value,
    lv.avgSpLpComparison.value,
    lv.avgSpOpAllProperties.value,
    lv.salesAbsorption.value,
    lv.avgDomCdom.value,
    lv.supplyAmpDemandNumUnits.value,
    lv.monthlySupplyOfInventory.value,
    lv.underContractUc.value,
    lv.forSaleVsFsldm.value,
    tableViewNames.agentMovement,
];

export const getPinnedRowTitle = (view) => {
    if (viewsToReplaceAgentName.includes(view)) return {[agentsColumns.agentName.id]: agentProductionTerms.summary};
    if (viewsToReplaceOfficeName.includes(view)) return {[agentsColumns.officeName.id]: searchTerms.totals};
    return {};
};

export const getOverviewPeriodConfig = (searchPeriod, timePeriod) =>({
    ...overviewPeriods[searchPeriod],
    listingId: listingIds[timePeriod],
});

const getGroupAndFilteredOptions = (config, groupTerm, value) => {
    const group = config.find(item => item?.group === groupTerm);
    const { options, ...rest } = group || {};
    const filteredOptions = options?.find(option => option?.value === value);
    return { filteredOptions, rest };
};

const createChartConfig = ({filteredOptions, overviewChartConfig, xAxisLegends, yAxisText,
    statusOptionsLabels, is6Months, customToolTipLabels, tabName}) => {

    const isInventory = tabName === tabNames.inventory.toLowerCase();
    const isPrices = tabName === tabNames.prices.toLowerCase();

    return {
        ...filteredOptions,
        label: listingOptions.overview.label,
        value: listingOptions.overview.value,
        isDefault: true,
        chart: {
            ...filteredOptions?.chart,
            lowHexColor: overviewChartConfig?.lowHexColor,
            highHexColor: overviewChartConfig?.highHexColor,
            auxHexColor: overviewChartConfig?.auxHexColor,
            threshold: overviewChartConfig?.threshold,
            xAxisLegends: xAxisLegends,
            yAxis: {
                ...filteredOptions?.chart?.yAxis,
                title: {
                    ...filteredOptions?.chart?.yAxis.title,
                    ...(yAxisText && { text: yAxisText })
                }
            }
        },
        statusOptions: {
            ...filteredOptions?.statusOptions,
            groups: [
                {
                    options: statusOptionsLabels.map((label, index) => ({
                        ...filteredOptions?.statusOptions?.groups[0]?.options.find(option => option.label === label),
                        ...(isPrices && customToolTipLabels && {label: customToolTipLabels[index]}),
                        ...(isInventory && is6Months && index === 0 && {
                            keyField: mdCols.ucAvgDom.id,
                            label: terms.avgDom,
                        }),
                        isDefault: true,
                    }))
                }
            ]
        }
    };
};

const chartConfigs = ({tabName, config, overviewChartConfig, timePeriod}) => {
    const configs = {
        inventory: {
            groupTerm: terms.salesRate,
            value: overviewChartConfig.listingId,
            xAxisLegends: overviewChartConfig?.xAxisLegends,
            yAxisText: overviewChartConfig?.yAxisText,
            statusOptionsLabels: filteredOptions => [filteredOptions?.statusOptions?.groups[0]?.options[0]?.label],
            is6Months: timePeriod === mdtp[mdTerms.months6Weekly],
            customToolTipLabels: null,
        },
        prices: {
            groupTerm: terms.price,
            value: listingStatuses.medianPrice.id,
            xAxisLegends: [legend.medianList, legend.medianSold],
            yAxisText: false,
            statusOptionsLabels: [optionLabels.forSale, optionLabels.sold],
            is6Months: false,
            customToolTipLabels: [labels.medianList, labels.medianSold],
        },
        activity: {
            groupTerm: terms.supplyAmpDemand,
            value: supplyAmpDemandNumUnits.value,
            xAxisLegends: [legend.forSale, legend.underContract, legend.sold],
            yAxisText: false,
            statusOptionsLabels: filteredOptions => [
                filteredOptions?.statusOptions?.groups[0]?.options[1]?.label,
                filteredOptions?.statusOptions?.groups[0]?.options[2]?.label,
                filteredOptions?.statusOptions?.groups[0]?.options[3]?.label
            ],
            is6Months: false,
            customToolTipLabels: null,
        }
    };

    const { groupTerm, value, xAxisLegends, yAxisText, statusOptionsLabels, is6Months,
        customToolTipLabels
    } = configs[tabName];
    const { filteredOptions, rest } = getGroupAndFilteredOptions(config, groupTerm, value);

    return { filteredOptions, rest, xAxisLegends, yAxisText, statusOptionsLabels, is6Months, customToolTipLabels };
};

export const getChartConfigByTabName = (tabName, config, overviewChartConfig, timePeriod) => {
    const { filteredOptions, rest, xAxisLegends, yAxisText, statusOptionsLabels, is6Months,
        customToolTipLabels
    } = chartConfigs({
        tabName, config, overviewChartConfig, timePeriod
    });

    const chartOptions = createChartConfig({
        filteredOptions,
        overviewChartConfig,
        xAxisLegends,
        yAxisText,
        statusOptionsLabels: typeof statusOptionsLabels === 'function'
            ? statusOptionsLabels(filteredOptions)
            : statusOptionsLabels,
        is6Months,
        customToolTipLabels,
        tabName,
    });

    return {
        ...rest,
        group: null,
        id: listingOptions.overview.value,
        options: [chartOptions],
        periodSelection: false,
    };
};

export const addOverviewConfig = (config, loadedSearchCriteria) => {
    if (!config?.length) return;

    const timePeriod = loadedSearchCriteria?.timePeriod?.intervalType;
    const monthlyPeriods = Object.keys(listingIds).slice(0, 2);
    const searchPeriod = monthlyPeriods.includes(timePeriod)
        ? overviewPeriods.monthly.value
        : overviewPeriods.other.value;
    const overviewChartConfig = getOverviewPeriodConfig(searchPeriod, timePeriod);

    const inventory = getChartConfigByTabName(tabNames.inventory.toLowerCase(), config, overviewChartConfig, timePeriod);
    const prices = getChartConfigByTabName(tabNames.prices.toLowerCase(), config, overviewChartConfig, timePeriod);
    const activity = getChartConfigByTabName(tabNames.activity.toLowerCase(), config, overviewChartConfig, timePeriod);

    return { inventory, prices, activity };
};

export const getBeginAndEndPeriodsData = (chartData) => {
    if (chartData.length < 2) return;
    return [chartData[0], chartData[chartData.length-1]];
};

export const getIncreaseDecreaseValue = (args) => {
    const { changeValuePercentage } = getQuarterlyOrMonthlyData(args);
    const changeValueFormattedUnits = formatUnits(changeValuePercentage * 100);
    const absChangeValue = Math.abs(getCleanedValue(changeValueFormattedUnits));
    const changeValueFormattedPercentage = formatPercentage(absChangeValue, 1);
    const isIncrease = getCleanedValue(changeValueFormattedUnits) >= 0;
    const label = isIncrease ? terms.increase : terms.decrease;

    return `${changeValueFormattedPercentage} ${label}`;
};
